import { ComponentProps, useContext } from "react";
import { Button as ReactAriaButton } from "react-aria-components";
import clsx from "clsx";
import { match } from "ts-pattern";

import { PalletLangContext } from "../lang-provider";

import Icon from "./icon";

import Select from "./forms/select";
import * as ss from "./forms/select-state";
import type { SelectState } from "./forms/select-state";
import styles from "./pagination.module.css";
import Search from "./search";

interface PaginationProps {
  "data-testid"?: string;
  currentPage: number;
  className?: string;
  totalPages: number;
  nextButton: {
    isDisabled: boolean;
    onPress: () => void;
  };
  previousButton: {
    isDisabled: boolean;
    onPress: () => void;
  };
  search?: ComponentProps<typeof Search>;
  jumpTo?: (page: number) => void;
}

function pageId(num: number): string {
  return `page-${num}`;
}

type PageOption = { id: string; label: string; key: string };

// Pure. `null` when there are no pages: `fixed` needs a non-empty list and
// `always` needs a current row.
function jumpToState(
  label: string,
  pageOptions: ReadonlyArray<PageOption>,
  currentPage: number,
): SelectState<PageOption> | null {
  const [first, ...rest] = pageOptions;
  if (first === undefined) return null;
  return ss.always({
    label: ss.visibleLabel({ text: label, orientation: "row" }),
    options: ss.fixed<PageOption>([first, ...rest]),
    selected: pageOptions[currentPage - 1] ?? first,
  });
}

function extractPageNum(key: string): number {
  const normalized = key.replace("page-", "");
  return parseInt(normalized, 10);
}

function Pagination({
  "data-testid": testId = "pallet-pagination",
  currentPage,
  totalPages,
  nextButton,
  previousButton,
  jumpTo,
  className,
  search,
}: PaginationProps) {
  const langContext = useContext(PalletLangContext);

  const pageOptions: Array<PageOption> = [];
  for (let i = 0; i < totalPages; i++) {
    pageOptions.push({ id: pageId(i), label: `${i + 1}`, key: pageId(i) });
  }

  const jumpState = jumpToState(
    langContext?.simpleTranslate("pagination.jumpTo") ?? "",
    pageOptions,
    currentPage,
  );

  return (
    <div data-testid={testId} className={clsx(styles.pagination, className)}>
      <div className={styles.countAndSearch}>
        <div className={styles.pageCount}>
          <div className={styles.currentPage}>{currentPage}</div>/
          <div>
            {totalPages} {langContext?.simpleTranslate("pagination.pages")}
          </div>
        </div>
        {search && (
          // `Search` fills its container and owns no width of its own (SR-301
          // phase 6, OQ-4), so the sizing lives on the wrapper. SR-301 phase 7
          // (OQ-1) replaced the inherited `width: 332px` -- which was only ever
          // `search.module.css`'s old input `min-width` written out -- with
          // `flex-1`, so the wrapper takes the slack `.countAndSearch` has left
          // after the page count instead of naming a number.
          <div className="flex-1">
            <Search
              autofocus={search?.autofocus || true}
              placeholder={search.placeholder}
              label={search.label}
              value={search.value}
              onChange={search.onChange}
              onClear={search.onClear}
            />
          </div>
        )}
      </div>

      <div className={styles.paginator}>
        <ReactAriaButton
          aria-label="Previous Page"
          isDisabled={previousButton.isDisabled}
          className={clsx(styles.button, styles.left)}
          onPress={previousButton.onPress}
        >
          <Icon name="ChevronLeft" size={24} />
        </ReactAriaButton>

        <ReactAriaButton
          aria-label="Next Page"
          isDisabled={nextButton.isDisabled}
          className={clsx(styles.button, styles.right)}
          onPress={nextButton.onPress}
        >
          <Icon name="ChevronRight" size={24} />
        </ReactAriaButton>

        {typeof jumpTo === "function" && jumpState && (
          <Select
            state={jumpState}
            className={styles.jumpToSelect}
            onMsg={(msg) =>
              match(msg)
                .with({ kind: "picked" }, ({ item }) =>
                  jumpTo(extractPageNum(item.id)),
                )
                .otherwise(() => {})
            }
          />
        )}
      </div>
    </div>
  );
}

export default Pagination;
